import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { IBaseProduct, ProductSearchFilter, API, productName } from '@getgreenline/homi-shared'
import { Select, Spin, message, Col, Row, Tooltip } from 'antd'
import { debounce } from 'lodash'
import { parseErrorMsg, GrRegex } from '../../utilities/helpers'
import { OptionProps } from 'antd/lib/select'
import { SmartImage } from '../SmartImage'
import classnames from 'classnames'

type SelectValueType<T> = T extends 'default' | undefined ? string : string[]

type SelectMode = 'default' | undefined | 'multiple' | 'tags'

interface Props {
  debounceTime?: number
  disabled?: boolean
  tooltipText?: string
  showSearch?: boolean
  mode: SelectMode
  className?: string
  placeholder?: string
  filterOption?:
    | boolean
    | ((inputValue: string, option: React.ReactElement<OptionProps>) => boolean)
  dropdownMatchSelectWidth?: boolean
  style?: React.CSSProperties
  allowClear?: boolean
  productIdsToHide?: string[]
  onChange: (products: IBaseProduct[]) => void
  onSelect?: (value: string | string[]) => void
  selectOptionClassName?: string
  containerParent?: HTMLElement
  selectedProducts?: string | string[]
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  isProductFetching: boolean
  foundProducts: Map<string, IBaseProduct>
  containerParentEl: HTMLElement | undefined
}

@inject('currentCompanyStore')
@observer
export class AsyncProductInput extends React.Component<Props & InjectedProps, State> {
  DEBOUNCE_TIME = this.props.debounceTime || 800

  MIN_PRODUCT_NAME_LENGTH = 2

  company = this.props.currentCompanyStore!.company!

  searchFilter = new ProductSearchFilter({
    selectedSupplierId: undefined,
    selectedCategoryIds: [],
    offset: 0,
    limit: 70,
  })

  constructor(props: Props & InjectedProps) {
    super(props)

    this.state = {
      isProductFetching: false,
      foundProducts: new Map<string, IBaseProduct>(),
      containerParentEl: undefined,
    }
  }

  componentDidMount() {
    const containerParentEl =
      this.props.containerParent || (document.getElementById('app-main-view') as HTMLElement) // type casting, HTMLElement expected which is a superset of Element which this returns

    if (containerParentEl) {
      this.setState({ containerParentEl })
    }
  }

  setIsProductFetching = (isProductFetching: boolean) => this.setState({ isProductFetching })

  setFoundProducts = (foundProducts: Map<string, IBaseProduct>) => this.setState({ foundProducts })

  populateProducts = (productIds: string[]): IBaseProduct[] => {
    const products: IBaseProduct[] = []

    productIds.forEach((productId) => {
      const foundProduct = this.state.foundProducts.get(productId)

      if (foundProduct) {
        products.push(foundProduct)
      }
    })

    return products
  }

  onSelectChange = (productIds: string | string[]) => {
    if (!Array.isArray(productIds)) {
      productIds = [productIds]
    }

    const products = this.populateProducts(productIds)
    this.props.onChange(products)
  }

  onSelectSearch = async (productName: string) => {
    const { foundProducts } = this.state

    const trimmedProductName = productName.trim().replace(GrRegex.unicodeRegex, '')

    try {
      if (trimmedProductName && trimmedProductName.length >= this.MIN_PRODUCT_NAME_LENGTH) {
        await this.debounceFetchProducts(trimmedProductName)
      } else {
        foundProducts.clear()
      }
    } catch (error) {
      message.error(
        parseErrorMsg(error) || `An error occurred while looking for "${trimmedProductName}"`,
      )
    }
  }

  fetchProduct = async (searchQuery = '') => {
    const { foundProducts } = this.state

    this.setIsProductFetching(true)

    this.searchFilter.searchQuery = searchQuery

    try {
      const products = await API.getByProductSearch(this.company.id, this.searchFilter)

      foundProducts.clear()

      products.forEach((product) => this.setFoundProducts(foundProducts.set(product.id, product)))
    } finally {
      this.setIsProductFetching(false)
    }
  }

  onSelectOption = (value: string) => {
    this.props.onSelect && this.props.onSelect(value)
  }

  debounceFetchProducts = debounce(this.fetchProduct, this.DEBOUNCE_TIME)

  get loader(): JSX.Element | null {
    return this.state.isProductFetching ? <Spin size='small' /> : null
  }

  get filteredFoundProductIds(): string[] {
    const foundProductIds = [...this.state.foundProducts.keys()]
    const productIdsToHide = this.props.productIdsToHide || []

    return foundProductIds.filter((foundProductId) => !productIdsToHide.includes(foundProductId))
  }

  get emptySearchResults(): boolean {
    return this.filteredFoundProductIds.length === 0 && !this.state.isProductFetching
  }

  render() {
    const {
      showSearch,
      mode,
      className,
      placeholder,
      filterOption,
      dropdownMatchSelectWidth,
      style,
      allowClear,
      selectedProducts,
    } = this.props
    const { foundProducts, containerParentEl } = this.state

    return (
      <Tooltip title={this.props.tooltipText}>
        <Select<SelectValueType<SelectMode>>
          showSearch={showSearch}
          mode={mode}
          disabled={this.props.disabled}
          dropdownMatchSelectWidth={!!dropdownMatchSelectWidth}
          className={className}
          placeholder={placeholder}
          filterOption={filterOption}
          notFoundContent={this.loader}
          onChange={this.onSelectChange}
          onSearch={this.onSelectSearch}
          style={style}
          optionLabelProp='label'
          allowClear={!!allowClear}
          onSelect={this.onSelectOption}
          value={selectedProducts}
          getPopupContainer={() => (containerParentEl ? containerParentEl : document.body)}
          dropdownStyle={this.emptySearchResults ? { display: 'none' } : undefined}
        >
          {this.filteredFoundProductIds.map((productId) => {
            const product = foundProducts.get(productId)!
            const fullProductName = productName(product)

            return (
              <Select.Option key={productId} value={productId} label={fullProductName}>
                <Row className={classnames('d-flex flex-row', this.props.selectOptionClassName)}>
                  <Col className='mr-3'>
                    <SmartImage src={product.imageUrl} width={30} height={30} />
                  </Col>

                  <Col>
                    <Row className='text-wrap'>{fullProductName}</Row>
                    {product.sku && (
                      <Row className='text-wrap'>
                        <small>
                          SKU: <b>{product.sku}</b>
                        </small>
                      </Row>
                    )}
                    {product.barcode && (
                      <Row className='text-wrap'>
                        <small>
                          Barcode: <b>{product.barcode}</b>
                        </small>
                      </Row>
                    )}
                  </Col>
                </Row>
              </Select.Option>
            )
          })}
        </Select>
      </Tooltip>
    )
  }
}
